import React, { useState, useEffect } from "react";
import {
  FaBullhorn,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaPlay,
  FaBookOpen,
  FaAward,
  FaLightbulb,
  FaExclamationCircle,
  FaRedoAlt,
} from "react-icons/fa";
import api from "../api/api";

const homeHeaderStyles = `
/* =========================================================
   SCHOOL HOME HEADER — PURPLE & GOLD DESIGN SYSTEM
========================================================= */

.school-home-header {
  --school-purple: #3a3af1;
  --school-purple-dark: #030147;
  --school-purple-deep: #030239;
  --school-purple-light: #ede9fe;
  --school-gold: #facc15;
  --school-gold-dark: #eab308;
  --school-white: #ffffff;
  --school-muted: #64748b;
  --school-border: #e9e3f5;

  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: minmax(0, 1.85fr) minmax(310px, 0.82fr);
  gap: 22px;
  width: 100%;
  min-height: 610px;
  padding: 24px;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 0% 0%, rgba(124, 58, 237, 0.10), transparent 42%),
    #f8f7fc;
  color: #211536;
  font-family: "Poppins", "Inter", system-ui, -apple-system, sans-serif;
  box-sizing: border-box;
}

.school-home-header *,
.school-home-header *::before,
.school-home-header *::after {
  box-sizing: border-box;
}

.school-home-header a {
  text-decoration: none;
}

.school-home-header button {
  font: inherit;
}

.school-home-header button:focus-visible,
.school-home-header a:focus-visible {
  outline: 3px solid var(--school-gold);
  outline-offset: 4px;
}

/* =========================================================
   MAIN HERO SLIDER
========================================================= */

.school-home-slider {
  position: relative;
  min-width: 0;
  min-height: 562px;
  height: 100%;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 26px;
  background: var(--school-purple-deep);
  box-shadow: 0 24px 65px rgba(45, 16, 83, 0.20);
}

.school-home-slider::after {
  position: absolute;
  z-index: 1;
  inset: 0;
  content: "";
  pointer-events: none;
  background:
    linear-gradient(
      90deg,
      rgba(0, 12, 106, 0.96) 0%,
      rgba(6, 0, 62, 0.86) 40%,
      rgba(27, 31, 84, 0.45) 72%,
      rgba(25, 10, 48, 0.12) 100%
    ),
    linear-gradient(
      0deg,
      rgba(20, 8, 38, 0.72) 0%,
      transparent 45%
    );
}

.school-home-slide {
  position: absolute;
  inset: 0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  opacity: 0;
  transform: scale(1.07);
  transition:
    opacity 900ms ease,
    transform 7000ms ease;
  pointer-events: none;
}

.school-home-slide.active {
  z-index: 0;
  opacity: 1;
  transform: scale(1);
}

.school-home-slider::before {
  position: absolute;
  z-index: 2;
  top: -120px;
  right: -120px;
  width: 330px;
  height: 330px;
  content: "";
  border: 1px solid rgba(250, 204, 21, 0.22);
  border-radius: 50%;
  box-shadow:
    0 0 0 36px rgba(250, 204, 21, 0.045),
    0 0 0 75px rgba(255, 255, 255, 0.025);
  pointer-events: none;
}

/* =========================================================
   HERO CONTENT
========================================================= */

.school-home-content {
  position: absolute;
  z-index: 3;
  top: 50%;
  left: 0;
  width: min(760px, 100%);
  padding: 42px 48px 78px;
  color: var(--school-white);
  opacity: 0;
  transform: translateY(calc(-50% + 22px));
  transition:
    opacity 550ms ease,
    transform 550ms ease;
  pointer-events: none;
}

.school-home-content.animate {
  opacity: 1;
  transform: translateY(-50%);
  pointer-events: auto;
}

.school-home-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  margin-bottom: 25px;
  padding: 10px 15px;
  border: 1px solid rgba(250, 204, 21, 0.40);
  border-radius: 999px;
  background: rgba(250, 204, 21, 0.12);
  color: #fde68a;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.9px;
  line-height: 1.4;
}

.school-home-eyebrow svg {
  flex-shrink: 0;
  color: var(--school-gold);
  font-size: 17px;
}

.school-home-title {
  max-width: 690px;
  margin: 0;
  color: var(--school-white);
  font-size: clamp(2.25rem, 4vw, 4.2rem);
  font-weight: 900;
  letter-spacing: -2.1px;
  line-height: 1.10;
  overflow-wrap: anywhere;
  text-wrap: balance;
}

.school-home-title::first-letter {
  color: inherit;
}

.school-home-gold-line {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  max-width: 270px;
  margin: 23px 0 19px;
}

.school-home-gold-line span {
  display: block;
  height: 4px;
  border-radius: 20px;
  background: var(--school-gold);
}

.school-home-gold-line span:first-child {
  width: 53px;
}

.school-home-gold-line span:last-child {
  width: 100%;
  max-width: 95px;
  opacity: 0.40;
}

.school-home-description {
  max-width: 570px;
  margin: 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.95;
  overflow-wrap: anywhere;
}

.school-home-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 13px;
  margin-top: 30px;
}

.school-home-primary,
.school-home-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 49px;
  padding: 13px 19px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 800;
  transition:
    transform 200ms ease,
    background 200ms ease,
    box-shadow 200ms ease;
  cursor: pointer;
}

.school-home-primary {
  border: 1px solid var(--school-gold);
  background: var(--school-gold);
  color: #30134b;
  box-shadow: 0 8px 24px rgba(250, 204, 21, 0.17);
}

.school-home-primary:hover {
  transform: translateY(-3px);
  background: #fde047;
  box-shadow: 0 13px 28px rgba(250, 204, 21, 0.25);
}

.school-home-primary svg {
  font-size: 13px;
  transition: transform 200ms ease;
}

.school-home-primary:hover svg {
  transform: translateX(4px);
}

.school-home-secondary {
  border: 1px solid rgba(255, 255, 255, 0.40);
  background: rgba(255, 255, 255, 0.075);
  color: #ffffff;
  backdrop-filter: blur(8px);
}

.school-home-secondary:hover {
  transform: translateY(-3px);
  background: rgba(255, 255, 255, 0.16);
}

.school-home-play-icon {
  display: grid;
  width: 25px;
  height: 25px;
  place-items: center;
  border-radius: 50%;
  background: var(--school-gold);
  color: var(--school-purple-dark);
  font-size: 10px;
}

.school-home-benefits {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 22px;
  margin-top: 31px;
}

.school-home-benefit {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.90);
  font-size: 11px;
  font-weight: 600;
}

.school-home-benefit svg {
  flex-shrink: 0;
  color: var(--school-gold);
  font-size: 14px;
}

/* =========================================================
   SLIDER NAVIGATION
========================================================= */

.school-home-navigation {
  position: absolute;
  z-index: 5;
  right: 23px;
  bottom: 25px;
  display: flex;
  gap: 9px;
}

.school-home-arrow {
  display: grid;
  width: 43px;
  height: 43px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.30);
  border-radius: 13px;
  background: rgba(37, 14, 63, 0.60);
  color: #ffffff;
  backdrop-filter: blur(10px);
  transition: all 200ms ease;
  cursor: pointer;
}

.school-home-arrow:hover {
  border-color: var(--school-gold);
  background: var(--school-gold);
  color: var(--school-purple-dark);
  transform: translateY(-2px);
}

.school-home-counter {
  position: absolute;
  z-index: 5;
  bottom: 31px;
  left: 32px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.70);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
}

.school-home-counter-current {
  color: var(--school-gold);
  font-size: 20px;
  font-weight: 900;
}

.school-home-counter-line {
  width: 39px;
  height: 2px;
  background: rgba(255, 255, 255, 0.40);
}

/* =========================================================
   RIGHT INFORMATION PANEL
========================================================= */

.school-home-sidebar {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
}

.school-home-announcement {
  position: relative;
  padding: 23px;
  overflow: hidden;
  border: 1px solid rgba(109, 40, 217, 0.10);
  border-radius: 22px;
  background:
    radial-gradient(circle at 100% 0%, rgba(250, 204, 21, 0.15), transparent 38%),
    linear-gradient(145deg, #ffffff, #fbf9ff);
  box-shadow: 0 10px 30px rgba(48, 24, 77, 0.055);
}

.school-home-announcement::after {
  position: absolute;
  top: 0;
  right: 25px;
  width: 58px;
  height: 4px;
  content: "";
  border-radius: 0 0 8px 8px;
  background: var(--school-gold);
}

.school-home-announcement-header {
  display: flex;
  align-items: center;
  gap: 13px;
  padding-bottom: 19px;
  border-bottom: 1px solid var(--school-border);
}

.school-home-announcement-icon {
  display: grid;
  width: 47px;
  height: 47px;
  flex-shrink: 0;
  place-items: center;
  border-radius: 15px;
  background: var(--school-purple-light);
  color: var(--school-purple);
  font-size: 19px;
}

.school-home-section-label {
  display: block;
  margin-bottom: 5px;
  color: var(--school-purple);
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 1.6px;
}

.school-home-announcement-heading {
  margin: 0;
  color: #27133f;
  font-size: 15px;
  font-weight: 850;
  line-height: 1.4;
}

.school-home-announcement-body {
  padding-top: 19px;
}

.school-home-announcement-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  color: #010048;
  font-size: 18px;
  font-weight: 850;
  line-height: 1.4;
}

.school-home-announcement-title svg {
  flex-shrink: 0;
  color: var(--school-gold-dark);
  font-size: 15px;
}

.school-home-announcement-description {
  margin: 0;
  color: var(--school-muted);
  font-size: 12px;
  line-height: 1.9;
}

.school-home-announcement-link {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-top: 19px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--school-purple);
  font-size: 12px;
  font-weight: 850;
  transition: gap 200ms ease;
  cursor: pointer;
}

.school-home-announcement-link:hover {
  gap: 14px;
  color: var(--school-purple-dark);
}

/* =========================================================
   FEATURE CARDS
========================================================= */

.school-home-features {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.school-home-feature {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
  padding: 18px;
  border: 1px solid rgba(109, 40, 217, 0.085);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(48, 24, 77, 0.035);
  transition:
    transform 220ms ease,
    border-color 220ms ease,
    box-shadow 220ms ease;
}

.school-home-feature:hover {
  transform: translateY(-3px);
  border-color: rgba(109, 40, 217, 0.25);
  box-shadow: 0 12px 26px rgba(48, 24, 77, 0.075);
}

.school-home-feature-icon {
  display: grid;
  width: 45px;
  height: 45px;
  flex-shrink: 0;
  place-items: center;
  border-radius: 14px;
  background: #f0e9ff;
  color: var(--school-purple);
  font-size: 18px;
}

.school-home-feature-icon.gold {
  background: #fff8d9;
  color: #9a6700;
}

.school-home-feature-copy {
  min-width: 0;
}

.school-home-feature-title {
  margin: 1px 0 7px;
  color: #0b0158;
  font-size: 13px;
  font-weight: 850;
  line-height: 1.5;
}

.school-home-feature-description {
  margin: 0;
  color: var(--school-muted);
  font-size: 11px;
  line-height: 1.8;
}

/* =========================================================
   SCHOOL MOTTO
========================================================= */

.school-home-motto {
  position: relative;
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
  padding: 17px 18px;
  overflow: hidden;
  border: 1px solid rgba(250, 204, 21, 0.25);
  border-radius: 17px;
  background: linear-gradient(115deg, #351052, #54228b);
  color: #ffffff;
}

.school-home-motto::after {
  position: absolute;
  right: -17px;
  bottom: -40px;
  width: 100px;
  height: 100px;
  content: "";
  border: 1px solid rgba(250, 204, 21, 0.22);
  border-radius: 50%;
  box-shadow: 0 0 0 15px rgba(250, 204, 21, 0.045);
}

.school-home-motto-star {
  display: grid;
  width: 41px;
  height: 41px;
  flex-shrink: 0;
  place-items: center;
  border-radius: 13px;
  background: var(--school-gold);
  color: var(--school-purple-dark);
  font-size: 17px;
}

.school-home-motto-copy {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.school-home-motto-title {
  display: block;
  margin-bottom: 6px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.5;
}

.school-home-motto-subtitle {
  display: block;
  color: #f6df9a;
  font-size: 9px;
  font-weight: 650;
  letter-spacing: 0.55px;
  line-height: 1.6;
}

/* =========================================================
   SLIDE INDICATORS
========================================================= */

.school-home-indicators {
  position: absolute;
  z-index: 7;
  top: 38px;
  left: 50%;
  display: flex;
  align-items: center;
  gap: 7px;
  max-width: 45%;
  transform: translateX(-50%);
}

.school-home-indicator {
  width: 22px;
  height: 5px;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.40);
  cursor: pointer;
  transition: width 250ms ease, background 250ms ease;
}

.school-home-indicator.active {
  width: 39px;
  background: var(--school-gold);
}

.school-home-indicator:hover {
  background: #fde68a;
}

/* =========================================================
   LOADING / ERROR / EMPTY STATES
========================================================= */

.school-home-state {
  --school-purple: #6d28d9;
  --school-purple-dark: #3b0764;
  --school-gold: #facc15;

  display: flex;
  min-height: 470px;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background:
    radial-gradient(circle at 10% 10%, rgba(109, 40, 217, 0.09), transparent 36%),
    #faf8ff;
  color: #020b4f;
  font-family: "Poppins", "Inter", system-ui, sans-serif;
  text-align: center;
  box-sizing: border-box;
}

.school-home-state-card {
  width: 100%;
  max-width: 420px;
  padding: 38px 28px;
  border: 1px solid #eee7fa;
  border-radius: 24px;
  background: #ffffff;
  box-shadow: 0 20px 55px rgba(53, 16, 82, 0.08);
}

.school-home-state-icon {
  display: grid;
  width: 65px;
  height: 65px;
  margin: 0 auto 20px;
  place-items: center;
  border-radius: 20px;
  background: #f0e9ff;
  color: var(--school-purple);
  font-size: 27px;
}

.school-home-state-icon.error {
  background: #fff0f0;
  color: #c62828;
}

.school-home-spinner {
  width: 31px;
  height: 31px;
  margin: 0 auto 19px;
  border: 3px solid #e9ddfb;
  border-top-color: var(--school-purple);
  border-right-color: var(--school-gold);
  border-radius: 50%;
  animation: school-home-spin 850ms linear infinite;
}

@keyframes school-home-spin {
  to {
    transform: rotate(360deg);
  }
}

.school-home-state-title {
  margin: 0 0 9px;
  color: #28163f;
  font-size: 21px;
  font-weight: 850;
  line-height: 1.45;
}

.school-home-state-description {
  margin: 0;
  color: #718096;
  font-size: 13px;
  line-height: 1.8;
}

.school-home-retry {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  margin-top: 22px;
  padding: 12px 19px;
  border: 0;
  border-radius: 11px;
  background: var(--school-purple);
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: background 200ms ease, transform 200ms ease;
}

.school-home-retry:hover {
  transform: translateY(-2px);
  background: var(--school-purple-dark);
}

/* =========================================================
   RESPONSIVE — LAPTOPS
========================================================= */

@media (min-width: 1500px) {
  .school-home-header {
    grid-template-columns: minmax(0, 1.95fr) minmax(340px, 0.8fr);
    gap: 26px;
    padding: 28px;
  }

  .school-home-slider {
    min-height: 625px;
  }

  .school-home-content {
    padding-right: 65px;
    padding-left: 58px;
  }

  .school-home-title {
    font-size: clamp(3.3rem, 3.4vw, 4.5rem);
  }

  .school-home-sidebar {
    gap: 19px;
  }

  .school-home-announcement {
    padding: 26px;
  }
}

@media (max-width: 1100px) {
  .school-home-header {
    grid-template-columns: minmax(0, 1.45fr) minmax(275px, 0.8fr);
    gap: 16px;
    padding: 16px;
  }

  .school-home-slider {
    min-height: 570px;
  }

  .school-home-content {
    padding: 35px 30px 78px;
  }

  .school-home-title {
    font-size: clamp(2.1rem, 4vw, 3.2rem);
    letter-spacing: -1.5px;
  }

  .school-home-description {
    font-size: 13px;
  }

  .school-home-announcement {
    padding: 18px;
  }

  .school-home-feature {
    padding: 14px;
  }

  .school-home-motto {
    padding: 14px;
  }
}

/* =========================================================
   RESPONSIVE — TABLETS
========================================================= */

@media (max-width: 850px) {
  .school-home-header {
    grid-template-columns: minmax(0, 1fr);
    gap: 17px;
    padding: 16px;
  }

  .school-home-slider {
    min-height: 550px;
  }

  .school-home-content {
    width: 100%;
    max-width: 710px;
    padding: 36px 34px 82px;
  }

  .school-home-title {
    max-width: 600px;
    font-size: clamp(2.5rem, 6vw, 3.7rem);
  }

  .school-home-description {
    max-width: 550px;
    font-size: 14px;
  }

  .school-home-sidebar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: start;
    gap: 14px;
  }

  .school-home-announcement {
    grid-row: span 2;
    height: 100%;
  }

  .school-home-features {
    gap: 12px;
  }

  .school-home-feature {
    padding: 15px;
  }

  .school-home-motto {
    align-self: end;
  }
}

/* =========================================================
   RESPONSIVE — MOBILE
========================================================= */

@media (max-width: 560px) {
  .school-home-header {
    gap: 15px;
    padding: 10px;
  }

  .school-home-slider {
    min-height: 535px;
    border-radius: 20px;
  }

  .school-home-slider::after {
    background:
      linear-gradient(
        90deg,
        rgba(25, 10, 48, 0.91),
        rgba(45, 18, 78, 0.69)
      ),
      linear-gradient(
        0deg,
        rgba(20, 8, 38, 0.68),
        transparent 70%
      );
  }

  .school-home-content {
    top: 48%;
    padding: 30px 21px 86px;
  }

  .school-home-content.animate {
    transform: translateY(-50%);
  }

  .school-home-eyebrow {
    gap: 8px;
    margin-bottom: 21px;
    padding: 9px 11px;
    font-size: 8px;
    letter-spacing: 1.2px;
  }

  .school-home-title {
    font-size: clamp(2rem, 8vw, 2.75rem);
    letter-spacing: -1.2px;
    line-height: 1.13;
  }

  .school-home-gold-line {
    margin: 18px 0 15px;
  }

  .school-home-description {
    font-size: 12px;
    line-height: 1.85;
  }

  .school-home-actions {
    align-items: stretch;
    gap: 10px;
    margin-top: 23px;
  }

  .school-home-primary,
  .school-home-secondary {
    min-height: 44px;
    padding: 11px 12px;
    border-radius: 10px;
    font-size: 10px;
  }

  .school-home-benefits {
    gap: 10px 14px;
    margin-top: 23px;
  }

  .school-home-benefit {
    gap: 6px;
    font-size: 9px;
  }

  .school-home-benefit svg {
    font-size: 12px;
  }

  .school-home-indicators {
    top: 20px;
    gap: 5px;
  }

  .school-home-indicator {
    width: 15px;
    height: 4px;
  }

  .school-home-indicator.active {
    width: 27px;
  }

  .school-home-navigation {
    right: 15px;
    bottom: 15px;
    gap: 7px;
  }

  .school-home-arrow {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }

  .school-home-counter {
    bottom: 24px;
    left: 18px;
    gap: 8px;
    font-size: 9px;
  }

  .school-home-counter-current {
    font-size: 17px;
  }

  .school-home-counter-line {
    width: 24px;
  }

  .school-home-sidebar {
    display: flex;
    flex-direction: column;
    gap: 13px;
  }

  .school-home-announcement {
    width: 100%;
    padding: 20px;
    border-radius: 18px;
  }

  .school-home-announcement-heading {
    font-size: 14px;
  }

  .school-home-announcement-title {
    font-size: 16px;
  }

  .school-home-announcement-description {
    font-size: 12px;
  }

  .school-home-features {
    grid-template-columns: minmax(0, 1fr);
    gap: 11px;
  }

  .school-home-feature {
    padding: 16px;
    border-radius: 16px;
  }

  .school-home-feature-title {
    font-size: 13px;
  }

  .school-home-feature-description {
    font-size: 11px;
  }

  .school-home-motto {
    width: 100%;
    padding: 17px;
  }
}

@media (max-width: 360px) {
  .school-home-content {
    padding-right: 15px;
    padding-left: 15px;
  }

  .school-home-title {
    font-size: 1.85rem;
  }

  .school-home-actions {
    flex-direction: column;
  }

  .school-home-primary,
  .school-home-secondary {
    width: 100%;
  }

  .school-home-benefits {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* =========================================================
   ACCESSIBILITY — REDUCED MOTION
========================================================= */

@media (prefers-reduced-motion: reduce) {
  .school-home-header *,
  .school-home-header *::before,
  .school-home-header *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
  }
}
`;

const HomeHeader = () => {
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  /* =====================================================
     FETCH SLIDES
  ===================================================== */

  useEffect(() => {
    let isMounted = true;

    const fetchSlides = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get("/api/slides/");
        const responseData = response.data;

        const data = Array.isArray(responseData)
          ? responseData
          : responseData?.results;

        if (isMounted) {
          setSlides(Array.isArray(data) ? data : []);
          setCurrentIndex(0);
        }
      } catch (err) {
        console.error("Failed to load school slides:", err);

        if (isMounted) {
          setError(
            "We could not load the school presentation. Please check your connection and try again."
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSlides();

    return () => {
      isMounted = false;
    };
  }, [retryCount]);

  /* =====================================================
     AUTOMATIC SLIDESHOW
  ===================================================== */

  useEffect(() => {
    if (slides.length <= 1 || loading || error) {
      return undefined;
    }

    const timer = setInterval(() => {
      setCurrentIndex((previous) => (previous + 1) % slides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [slides.length, loading, error]);

  /* =====================================================
     TEXT TRANSITION
  ===================================================== */

  useEffect(() => {
    setIsVisible(false);

    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timeout);
  }, [currentIndex]);

  /* =====================================================
     SLIDER CONTROLS
  ===================================================== */

  const goToNext = () => {
    if (!slides.length) return;

    setCurrentIndex((previous) => (previous + 1) % slides.length);
  };

  const goToPrevious = () => {
    if (!slides.length) return;

    setCurrentIndex(
      (previous) => (previous - 1 + slides.length) % slides.length
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const retryLoading = () => {
    setRetryCount((previous) => previous + 1);
  };

  /* =====================================================
     LOADING STATE
  ===================================================== */

  if (loading) {
    return (
      <>
        <style>{homeHeaderStyles}</style>

        <section
          className="school-home-state"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="school-home-state-card">
            <div className="school-home-state-icon">
              <FaGraduationCap />
            </div>

            <div className="school-home-spinner" />

            <h2 className="school-home-state-title">
              Preparing Your Learning Experience
            </h2>

            <p className="school-home-state-description">
              Please wait while we load the latest school information.
            </p>
          </div>
        </section>
      </>
    );
  }

  /* =====================================================
     ERROR STATE
  ===================================================== */

  if (error) {
    return (
      <>
        <style>{homeHeaderStyles}</style>

        <section className="school-home-state" role="alert">
          <div className="school-home-state-card">
            <div className="school-home-state-icon error">
              <FaExclamationCircle />
            </div>

            <h2 className="school-home-state-title">
              Something Went Wrong
            </h2>

            <p className="school-home-state-description">{error}</p>

            <button
              type="button"
              className="school-home-retry"
              onClick={retryLoading}
            >
              <FaRedoAlt />
              Try Again
            </button>
          </div>
        </section>
      </>
    );
  }

  /* =====================================================
     EMPTY STATE
  ===================================================== */

  if (slides.length === 0) {
    return (
      <>
        <style>{homeHeaderStyles}</style>

        <section className="school-home-state">
          <div className="school-home-state-card">
            <div className="school-home-state-icon">
              <FaGraduationCap />
            </div>

            <h2 className="school-home-state-title">
              Welcome to Our School
            </h2>

            <p className="school-home-state-description">
              Our school presentation is being prepared. Please check
              back soon for the latest information.
            </p>

            <button
              type="button"
              className="school-home-retry"
              onClick={retryLoading}
            >
              <FaRedoAlt />
              Refresh Slides
            </button>
          </div>
        </section>
      </>
    );
  }

  const currentSlide = slides[currentIndex];

  return (
    <>
      <style>{homeHeaderStyles}</style>

      <header
        className="school-home-header"
        aria-label="School introduction and highlights"
      >
        {/* ===============================================
            MAIN HERO SLIDER
        =============================================== */}

        <section className="school-home-slider" aria-roledescription="carousel">
          {slides.map((slide, index) => (
            <div
              key={slide.id || index}
              className={`school-home-slide ${
                currentIndex === index ? "active" : ""
              }`}
              style={{
                backgroundImage: slide.image
                  ? `url("${String(slide.image).replace(/"/g, '\\"')}")`
                  : "linear-gradient(135deg, #3b0764, #6d28d9)",
              }}
              aria-hidden={currentIndex !== index}
            />
          ))}

          {/* SLIDE INDICATORS */}

          {slides.length > 1 && (
            <div
              className="school-home-indicators"
              aria-label="Choose a presentation slide"
            >
              {slides.map((slide, index) => (
                <button
                  key={slide.id || index}
                  type="button"
                  className={`school-home-indicator ${
                    currentIndex === index ? "active" : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={currentIndex === index ? "true" : undefined}
                />
              ))}
            </div>
          )}

          {/* HERO TEXT */}

          <div
            key={currentSlide.id || currentIndex}
            className={`school-home-content ${
              isVisible ? "animate" : ""
            }`}
          >
            <div className="school-home-eyebrow">
              <FaGraduationCap />
              <span>EXCELLENCE IN EDUCATION</span>
            </div>

            <h1 className="school-home-title">
              {currentSlide.title || "Inspiring Excellence, Building Futures"}
            </h1>

            <div className="school-home-gold-line" aria-hidden="true">
              <span />
              <span />
            </div>

            <p className="school-home-description">
              {currentSlide.description ||
                "Discover a learning community dedicated to academic excellence, personal growth, strong character, and preparing students for a brighter future."}
            </p>

            <div className="school-home-actions">
              <a href="#about" className="school-home-primary">
                Discover Our School
                <FaArrowRight />
              </a>

              <a href="#about" className="school-home-secondary">
                <span className="school-home-play-icon">
                  <FaPlay />
                </span>
                Explore Our Mission
              </a>
            </div>

            <div className="school-home-benefits">
              <div className="school-home-benefit">
                <FaCheckCircle />
                <span>Quality Education</span>
              </div>

              <div className="school-home-benefit">
                <FaCheckCircle />
                <span>Student Excellence</span>
              </div>

              <div className="school-home-benefit">
                <FaCheckCircle />
                <span>Strong Character</span>
              </div>
            </div>
          </div>

          {/* SLIDE COUNTER */}

          {slides.length > 1 && (
            <div className="school-home-counter" aria-live="polite">
              <span className="school-home-counter-current">
                {String(currentIndex + 1).padStart(2, "0")}
              </span>

              <span className="school-home-counter-line" />

              <span>{String(slides.length).padStart(2, "0")}</span>
            </div>
          )}

          {/* PREVIOUS / NEXT */}

          {slides.length > 1 && (
            <div className="school-home-navigation">
              <button
                type="button"
                className="school-home-arrow"
                onClick={goToPrevious}
                aria-label="Previous slide"
              >
                <FaChevronLeft />
              </button>

              <button
                type="button"
                className="school-home-arrow"
                onClick={goToNext}
                aria-label="Next slide"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </section>

        {/* ===============================================
            SCHOOL INFORMATION SIDEBAR
        =============================================== */}

        <aside className="school-home-sidebar">
          {/* ANNOUNCEMENT */}

          <section
            className="school-home-announcement"
            aria-labelledby="school-announcement-heading"
          >
            <div className="school-home-announcement-header">
              <div className="school-home-announcement-icon">
                <FaBullhorn />
              </div>

              <div>
                <span className="school-home-section-label">
                  SCHOOL UPDATES
                </span>

                <h2
                  id="school-announcement-heading"
                  className="school-home-announcement-heading"
                >
                  Latest Announcement
                </h2>
              </div>
            </div>

            <div className="school-home-announcement-body">
              <h3 className="school-home-announcement-title">
                <FaStar />
                Admissions Are Open
              </h3>

              <p className="school-home-announcement-description">
                Give your child the opportunity to learn, grow, and
                succeed. Parents and guardians are invited to begin the
                admission process for the upcoming academic year.
              </p>

              <a
                href="#admissions"
                className="school-home-announcement-link"
              >
                Learn More
                <FaArrowRight />
              </a>
            </div>
          </section>

          {/* ACADEMIC FEATURES */}

          <div className="school-home-features">
            <article className="school-home-feature">
              <div className="school-home-feature-icon">
                <FaBookOpen />
              </div>

              <div className="school-home-feature-copy">
                <h3 className="school-home-feature-title">
                  Academic Excellence
                </h3>

                <p className="school-home-feature-description">
                  Building strong foundations through quality teaching,
                  discipline, and a passion for lifelong learning.
                </p>
              </div>
            </article>

            <article className="school-home-feature">
              <div className="school-home-feature-icon gold">
                <FaChalkboardTeacher />
              </div>

              <div className="school-home-feature-copy">
                <h3 className="school-home-feature-title">
                  Dedicated Educators
                </h3>

                <p className="school-home-feature-description">
                  Supportive teachers committed to helping every learner
                  discover their potential.
                </p>
              </div>
            </article>
          </div>

          {/* SCHOOL VALUES */}

          <div className="school-home-motto">
            <div className="school-home-motto-star">
              <FaAward />
            </div>

            <div className="school-home-motto-copy">
              <strong className="school-home-motto-title">
                Inspiring Tomorrow's Leaders
              </strong>

              <span className="school-home-motto-subtitle">
                KNOWLEDGE · CHARACTER · EXCELLENCE
              </span>
            </div>
          </div>
        </aside>
      </header>
    </>
  );
};

export default HomeHeader;